import React, { useContext, useEffect, useState } from 'react';
import OutlineButton from 'Components/Common/components/Button/OutlineButton';
import { toast } from 'react-toastify';
import ModalFooter from 'Components/Common/Modal/ModalFooter';
import ModalBody from 'Components/Common/Modal/ModalBody';
import ModalHeader from 'Components/Common/Modal/ModalHeader';
import ModalContainer from 'Components/Common/Modal/ModalContainer';
import { ProductsContext } from '../ProductsContext';
import FillButton from 'Components/Common/components/Button/FillButton';
import { deleteParentById } from 'api/Parent';
import { listChildProductsByParentId } from 'api/Products';

interface DialogProps {
  isOpen: boolean
  onClose: () => void;
  fetchData: () => void
}

const DeleteParentModal: React.FC<DialogProps> = ({ isOpen, onClose, fetchData }) => {
  const [loading, setLoading] = useState<boolean>(false)

  const { deleteParent } = useContext(ProductsContext)
  const [variants, setVariants] = useState<{name: string}[]|null>(null)

  useEffect(() => {
    if(!deleteParent?.id) return
    setVariants(null)

    listChildProductsByParentId(deleteParent.id)
    .then(({data}) => {
      setVariants(data)
    })
    .catch(() => {
      setVariants(null)
      toast.error("Failed to load variants")
    })
  }, [deleteParent?.id])

  const handleSubmit = async () => {
      if(!deleteParent) return toast.error("No parent selected")
      setLoading(true)

      deleteParentById(deleteParent.id)
      .then((res) => {
        onClose()
        toast.success(res.data?.message)
        fetchData()
      })
      .catch((err) => toast.error(err?.message ?? "Failed to delete parent"))
      .finally(() => setLoading(false))      
  }

  if(!deleteParent) return <></>

  return (
    <ModalContainer isOpen={isOpen} setOpen={onClose} width='500px'>
      <ModalHeader>
      <h2 className={`tw-text-sm tw-font-semibold tw-my-2  tw-flex tw-items-center tw-space-x-3`}>
        <span>Remove Child</span>
        <span className={`tw-top-[116px] tw-w-max tw-left-[475px] tw-rounded-[44px] tw-bg-[#DFE1E6] tw-h-[26px] tw-flex tw-flex-row tw-py-0.5 tw-px-3 tw-box-border tw-items-center tw-justify-center tw-text-slategray-200`}>
          <span className={`tw-text-sm tw-font-normal tw-text-[#505F79`}>{variants ? `${variants?.length} variants Included` : "Loading variants"} </span>
        </span>
      </h2>
      </ModalHeader>

      <ModalBody>
      <div className={`tw-box-border tw-w-auto tw-max-h-screen tw-overflow-auto`} /> 
      <div className={`tw-my-3`}>
         <div className="tw-border tw-bg-alert tw-border-alert tw-p-2.5 tw-flex tw-items-center tw-text-xs tw-space-x-2.5">
           <span className="w-[24px]">
             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
               <path d="M10.2898 4.35923L1.81978 18.4992C1.64514 18.8017 1.55274 19.1445 1.55177 19.4937C1.55079 19.843 1.64127 20.1864 1.8142 20.4898C1.98714 20.7931 2.2365 21.046 2.53748 21.2231C2.83847 21.4002 3.18058 21.4954 3.52978 21.4992H20.4698C20.819 21.4954 21.1611 21.4002 21.4621 21.2231C21.7631 21.046 22.0124 20.7931 22.1854 20.4898C22.3583 20.1864 22.4488 19.843 22.4478 19.4937C22.4468 19.1445 22.3544 18.8017 22.1798 18.4992L13.7098 4.35923C13.5315 4.06533 13.2805 3.82235 12.981 3.65371C12.6814 3.48508 12.3435 3.39648 11.9998 3.39648C11.656 3.39648 11.3181 3.48508 11.0186 3.65371C10.7191 3.82235 10.468 4.06533 10.2898 4.35923Z" stroke="#FFC400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
               <path d="M12 9.5V13.5" stroke="#FFC400" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
               <path d="M12 17.5H12.01" stroke="#FFC400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
             </svg>
           </span>
            <p>Deleting the parent &quot;{deleteParent?.name}&quot; the system will delete the parent and all its variant products (Child).</p> 
             {/* @todo display parent names */}
             {/* from the parent "SRAM POWERMETER ASSY RIVAL,"  */}
          
         </div>
     </div>

    <div className="tw-space-y-3">
      <div >
        <h6 className="tw-text-xs tw-text-slategray">Parent</h6>
        <p className="tw-leading-7">{deleteParent?.name}</p>
      </div>

      <div >
        <h6 className="tw-text-xs tw-text-slategray">Variants (Child)</h6>
        {
          variants === null && 
          <div className="tw-animate-pulse tw-text-xs py-1">
            Loading..
          </div>
        }
        <div className="">
          <div className="tw-overflow-auto tw-max-h-[250px] tw-rounded">
            {
              variants?.map((item, key) => {
                return(
                  <p key={key} className="tw-leading-7">{item.name}</p>
                )
              })
            }
          </div>
          </div>
      </div>

    </div>

      </ModalBody>
      <ModalFooter>
        <div className="tw-space-x-3 tw-text-right tw-flex tw-justify-end">
          <OutlineButton onClick={() => onClose()}>Cancel</OutlineButton>
          <FillButton buttonStyle='danger' disabled={(loading || !deleteParent)} onClick={handleSubmit}>Delete</FillButton>
        </div>
      </ModalFooter>
    </ModalContainer>
  );
};

export default DeleteParentModal;